import { useEffect, useState } from "react";
import { type QuoteListRow } from "~/features/orders/quoteList/useQuoteListState";
import { useOrderListData } from "~/features/orders/orderList/components/OrderListTable/useOrderListData";
import { OrderDataSort } from "~/features/orders/orderList/components/OrderListTable/orderDataSorting";
import { QUOTE_DATA_SORT_HANDLERS } from "~/features/orders/quoteList/components/QuoteListTable/quoteDataSorting";
import type { QuoteListSelectionState } from "~/features/orders/common/components/v2/QuoteTable/useQuoteListSelectionState";
import { downloadFile } from "~/common/utils/ApiHelper";
import { Box, Stack } from "~/common/mui";
import { useWindowVirtualizer } from "@tanstack/react-virtual";
import { QuoteCard } from "./QuoteCard";

interface QuoteListTableProps {
  rows: QuoteListRow[];
  toggleOpen: (row: QuoteListRow) => void;
  toggleOpenAll: () => void;
  toggleSelectAllQuotes: () => void;
  toggleSelectQuote: (row: QuoteListRow) => void;
  toggleSelectLineItem: (soNumber: string, quoteLineNo: string) => void;
  selectionPerDocument: QuoteListSelectionState["selectionPerDocument"];
}

export const QuoteListTableV2 = ({
  rows,
  toggleOpen,
  selectionPerDocument,
  toggleSelectLineItem,
  toggleSelectQuote,
}: QuoteListTableProps) => {
  const { sortedRows } = useOrderListData({
    rows,
    sortHandlers: QUOTE_DATA_SORT_HANDLERS,
    initialSortCode: OrderDataSort.DatePlaced,
    translationPrefix: "quoteListPage.table.header.",
  });

  const [loading, setLoading] = useState({ id: "", state: false });

  const downloadQuoteDocument = async (invoiceUrl: string, id: string) => {
    if (invoiceUrl !== "" && id !== "") {
      await downloadFile(`${invoiceUrl}?id=${id}`);
    }
  };

  useEffect(() => {
    if (loading.state) {
      const row = sortedRows.filter((row) => row.data.quoteNumber === loading.id)?.[0];
      if (row.detailsLoaded) {
        if (!!row && row.details?.soNumber) {
          const invoiceUrl = row.details?.quoteDocumentUrl || "";
          const quoteNumber = row.details?.soNumber;
          if (invoiceUrl !== "" && quoteNumber !== "") {
            downloadQuoteDocument(invoiceUrl, quoteNumber).then(() => {
              setLoading({ id: row.data.quoteNumber, state: false });
            });
          }
        } else {
          setLoading({ id: loading.id, state: false });
        }
      }
    }
  }, [sortedRows, loading.state]);

  const rowVirtualizer = useWindowVirtualizer({
    count: sortedRows.length,
    getItemKey: (index) => `${sortedRows[index]?.data.soNumber}-${index}`,
    estimateSize: () => 156,
    overscan: 8,
    scrollPaddingStart: 200,
    scrollPaddingEnd: 200,
  });

  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalVirtualHeight = rowVirtualizer.getTotalSize();
  const paddingTop = virtualRows.length > 0 ? virtualRows[0].start : 0;
  const paddingBottom = virtualRows.length > 0 ? totalVirtualHeight - virtualRows[virtualRows.length - 1].end : 0;

  return (
    <Stack
      sx={{
        width: "100%",
        position: "relative",
      }}
    >
      <Box sx={{ width: "100%" }}>
        <Box sx={{ height: `${paddingTop}px` }} />
        {virtualRows.map((virtualRow) => {
          const row = sortedRows[virtualRow.index];

          if (!row) {
            return null;
          }

          return (
            <Box
              key={virtualRow.key}
              data-index={virtualRow.index}
              ref={rowVirtualizer.measureElement}
              sx={{
                width: "100%",
                pb: virtualRow.index === sortedRows.length - 1 ? 0 : 2,
              }}
            >
              <QuoteCard
                row={row}
                toggleOpen={toggleOpen}
                toggleSelectQuote={toggleSelectQuote}
                toggleSelectLineItem={toggleSelectLineItem}
                selectionPerDocument={selectionPerDocument}
              />
            </Box>
          );
        })}
        <Box sx={{ height: `${paddingBottom}px` }} />
      </Box>
    </Stack>
  );
};
